import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../api';
import { fetchAuthSession } from 'aws-amplify/auth';

const DeityPractice = () => {
    const { deityId } = useParams();
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [deity, setDeity] = useState(null);
    const [practices, setPractices] = useState([]);
    const [selectedPractice, setSelectedPractice] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showGate, setShowGate] = useState(false);

    // Empowerment request form state
    const [requestReason, setRequestReason] = useState('');
    const [teacherReference, setTeacherReference] = useState('');
    const [requestStatus, setRequestStatus] = useState(null); // null | 'submitting' | 'success' | 'error' | 'duplicate'
    const [requestError, setRequestError] = useState('');

    useEffect(() => {
        const loadDeityAndPractices = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const { tokens } = await fetchAuthSession();
                const token = tokens?.idToken?.toString();
                const headers = {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                };

                // Fetch all deities and practices in parallel
                // Note: /temple/deities/{id} is not yet deployed; use list + filter
                const [deitiesRes, practicesRes] = await Promise.all([
                    fetch(`${API_URL}/temple/deities`, { headers }),
                    fetch(`${API_URL}/temple/practices?deityId=${encodeURIComponent(deityId)}`, { headers }),
                ]);

                if (!deitiesRes.ok) {
                    throw new Error(`Failed to load deities: ${deitiesRes.status}`);
                }

                if (!practicesRes.ok) {
                    throw new Error(`Failed to load practices: ${practicesRes.status}`);
                }

                const deitiesData = await deitiesRes.json();
                const practicesData = await practicesRes.json();

                const matchedDeity = (deitiesData.deities || []).find(d => d.id === deityId) || null;
                setDeity(matchedDeity);
                setPractices(practicesData.practices || []);
            } catch (err) {
                console.error('Error loading deity and practices:', err);
                setError('Could not load deity practices. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };

        loadDeityAndPractices();
    }, [deityId]);

    const handlePracticeSelect = (practice) => {
        if (practice.isProtected && !user?.empowerments?.includes(deityId)) {
            setShowGate(true);
            setSelectedPractice(practice);
            // Reset request form state when gate opens
            setRequestReason('');
            setTeacherReference('');
            setRequestStatus(null);
            setRequestError('');
        } else {
            navigate(`/temple/${deityId}/${practice.id}`);
        }
    };

    const handleRequestSubmit = async (e) => {
        e.preventDefault();

        if (!isAuthenticated || !user) {
            return;
        }

        setRequestStatus('submitting');
        setRequestError('');

        try {
            const res = await fetch(`${API_URL}/temple/empowerments`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${user.idToken}`,
                },
                body: JSON.stringify({
                    userId: user.userId,
                    userEmail: user.email,
                    userName: user.displayName,
                    deityId,
                    deityName: deity?.name || deityId,
                    lineageKey: deity?.lineageKey || 'general',
                    teacherReference: teacherReference.trim(),
                    reason: requestReason,
                    notes: requestReason,
                }),
            });

            if (res.status === 409) {
                setRequestStatus('duplicate');
                return;
            }

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.message || `Request failed: ${res.status}`);
            }

            setRequestStatus('success');
        } catch (err) {
            console.error('Error submitting empowerment request:', err);
            setRequestStatus('error');
            setRequestError(err.message || 'Something went wrong. Please try again.');
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 text-white flex items-center justify-center">
                <div className="text-2xl">Loading practices...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 text-white flex items-center justify-center">
                <div className="text-center space-y-4">
                    <p className="text-red-400 text-lg">{error}</p>
                    <Link to="/temple" className="text-blue-400 hover:text-blue-300">
                        Return to Temple
                    </Link>
                </div>
            </div>
        );
    }

    if (!deity) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 text-white flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl mb-4">Deity not found</h1>
                    <Link to="/temple" className="text-blue-400 hover:text-blue-300">
                        Return to Temple
                    </Link>
                </div>
            </div>
        );
    }

    if (showGate) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 text-white flex items-center justify-center">
                <div className="max-w-2xl mx-auto p-8">
                    <div className="bg-gray-800 bg-opacity-90 rounded-lg p-8">
                        <h2 className="text-3xl font-bold mb-6 text-center">Sacred Gateway</h2>

                        <div className="mb-6 p-4 bg-amber-900 bg-opacity-30 rounded-lg border border-amber-600">
                            <h3 className="text-lg font-semibold mb-2 text-amber-300">Empowerment Required</h3>
                            <p className="text-amber-100 text-sm">
                                This practice requires proper empowerment and transmission. The digital realm can support
                                your practice but cannot grant the empowerment itself.
                            </p>
                        </div>

                        <div className="space-y-4 mb-6">
                            <h4 className="font-semibold">{selectedPractice?.title}</h4>
                            <p className="text-gray-300">{selectedPractice?.description}</p>

                            <div className="space-y-2">
                                <p className="text-sm text-gray-400">
                                    <strong>Lineage:</strong> {deity.lineageKey || 'General Buddhist'}
                                </p>
                                <p className="text-sm text-gray-400">
                                    <strong>Requirements:</strong> Formal empowerment from qualified teacher
                                </p>
                            </div>
                        </div>

                        {/* Empowerment Request Section */}
                        {!isAuthenticated ? (
                            <div className="p-4 bg-blue-900 bg-opacity-30 rounded-lg border border-blue-600 mb-6">
                                <p className="text-blue-200 text-sm">
                                    To request access to this practice, please email us at{' '}
                                    <a href="mailto:info@buddhawisdom.org" className="text-blue-300 underline">
                                        info@buddhawisdom.org
                                    </a>
                                    {' '}and describe your lineage connection and empowerment history.
                                </p>
                            </div>
                        ) : requestStatus === 'success' ? (
                            <div className="p-4 bg-green-900 bg-opacity-30 rounded-lg border border-green-600 mb-6">
                                <p className="text-green-300 font-semibold mb-1">Request submitted successfully</p>
                                <p className="text-green-200 text-sm">
                                    Your request will be reviewed by our administrators. You will receive access once your lineage is verified and approved.
                                </p>
                            </div>
                        ) : requestStatus === 'duplicate' ? (
                            <div className="p-4 bg-yellow-900 bg-opacity-30 rounded-lg border border-yellow-600 mb-6">
                                <p className="text-yellow-300 font-semibold mb-1">Request already submitted</p>
                                <p className="text-yellow-200 text-sm">
                                    Your request is pending review. Please wait for our administrators to respond.
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={handleRequestSubmit} className="mb-6">
                                <h4 className="font-semibold mb-3 text-gray-200">Request Access</h4>
                                <p className="text-sm text-gray-400 mb-3">
                                    To request access, please provide the name of the qualified teacher who granted you this empowerment, and briefly describe your lineage connection.
                                </p>
                                <div className="mb-3">
                                    <label className="block text-xs text-gray-400 mb-1">
                                        Teacher who granted the empowerment <span className="text-red-400">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="e.g. Lama Tenzin, Kagyu Center, 2023"
                                        value={teacherReference}
                                        onChange={(e) => setTeacherReference(e.target.value)}
                                        required
                                        minLength={5}
                                        disabled={requestStatus === 'submitting'}
                                    />
                                </div>
                                <label className="block text-xs text-gray-400 mb-1">
                                    Describe your lineage connection (2–3 sentences) <span className="text-red-400">*</span>
                                </label>
                                <textarea
                                    className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white text-sm resize-none mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    rows={4}
                                    placeholder="e.g. I received the Vajrayogini empowerment from Lama Tenzin at Kagyu Center in 2023 and have been practicing sadhana regularly since then."
                                    value={requestReason}
                                    onChange={(e) => setRequestReason(e.target.value)}
                                    required
                                    minLength={20}
                                    disabled={requestStatus === 'submitting'}
                                />
                                {requestStatus === 'error' && (
                                    <p className="text-red-400 text-sm mb-3">{requestError}</p>
                                )}
                                <button
                                    type="submit"
                                    disabled={requestStatus === 'submitting' || requestReason.trim().length < 20 || teacherReference.trim().length < 5}
                                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed px-6 py-2 rounded-lg font-semibold transition-colors"
                                >
                                    {requestStatus === 'submitting' ? 'Submitting...' : 'Submit Request'}
                                </button>
                            </form>
                        )}

                        <div className="flex gap-4">
                            <button
                                onClick={() => setShowGate(false)}
                                className="flex-1 bg-gray-600 hover:bg-gray-700 px-6 py-2 rounded-lg font-semibold transition-colors"
                            >
                                Go Back
                            </button>
                        </div>

                        <div className="mt-6 text-center">
                            <Link
                                to="/temple"
                                className="text-blue-400 hover:text-blue-300 text-sm"
                            >
                                Return to Temple
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 text-white">
            {/* Header */}
            <div className="px-6 py-8">
                <Link
                    to="/temple"
                    className="text-blue-400 hover:text-blue-300 mb-4 inline-block"
                >
                    Back to Temple
                </Link>

                <div className="text-center">
                    <h1 className="text-4xl font-bold mb-4">{deity.name}</h1>
                    <p className="text-xl text-gray-300 max-w-2xl mx-auto">{deity.description}</p>
                </div>
            </div>

            {/* Practices */}
            <div className="max-w-4xl mx-auto px-6 pb-16">
                <h2 className="text-2xl font-bold mb-8 text-center">Select a Practice</h2>

                {practices.length === 0 ? (
                    <div className="text-center text-gray-400 py-16">
                        <p className="text-xl">No practices available for this deity yet.</p>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 gap-6">
                        {practices.map((practice) => (
                            <div
                                key={practice.id}
                                className="bg-gray-800 bg-opacity-90 rounded-lg p-6 hover:bg-opacity-100 transition-all cursor-pointer group"
                                onClick={() => handlePracticeSelect(practice)}
                            >
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-xl font-semibold group-hover:text-blue-300 transition-colors">
                                        {practice.title}
                                    </h3>
                                    <div className="flex items-center space-x-2">
                                        {practice.isProtected && (
                                            <span className="text-amber-400 text-sm">&#x1F512;</span>
                                        )}
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                            practice.mood === 'peaceful'
                                                ? 'bg-blue-600 bg-opacity-20 text-blue-300'
                                                : 'bg-red-600 bg-opacity-20 text-red-300'
                                        }`}>
                                            {practice.mood}
                                        </span>
                                    </div>
                                </div>

                                <p className="text-gray-300 mb-4">{practice.description}</p>

                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-400">
                                        Duration: {typeof practice.duration === 'number'
                                            ? `${Math.round(practice.duration / 60)} min`
                                            : practice.duration}
                                    </span>
                                    <div className="text-blue-400 group-hover:text-blue-300 transition-colors">
                                        Enter Practice
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Practice Notes */}
                <div className="mt-12 bg-gray-800 bg-opacity-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-4">Practice Guidelines</h3>
                    <div className="space-y-3 text-gray-300">
                        <p>Find a quiet, clean space where you won't be disturbed</p>
                        <p>Sit comfortably with your spine straight</p>
                        <p>Use headphones for the best audio experience</p>
                        <p>Allow full-screen mode for immersive visuals</p>
                        <p>Practice with sincere motivation and devotion</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeityPractice;
